import React, { type JSX } from 'react';

import { useQuery } from '@apollo/client/react';
import { useLocation } from 'react-router';

import { GetOrganizationDocument } from '../graphql/queries/__generated__/GetOrganization';
import { OD_HOMEPAGE_URL } from '../helpers/globals';
import trackEvent from '../helpers/trackEvent';
import ErrorPage from './ErrorPage';
import { REFERRAL_DASHBOARD_NAME } from './ReferralDashboard/ReferralDashboard';

export interface IUnauthorizedAccessErrorPageProps {
  dashboardName?: string;
  userEmail: string;
  loadingScreen: JSX.Element | null;
}

const UnauthorizedAccessErrorPage: React.FC<IUnauthorizedAccessErrorPageProps> = (
  props: IUnauthorizedAccessErrorPageProps,
) => {
  const location = useLocation();
  const { loading, error, data } = useQuery(GetOrganizationDocument);

  const errorEmail = 'partnerships@opendoor.com';
  const emailBlurb = (
    <p>
      If you believe this is an error, email us at <a href={`mailto:${errorEmail}`}>{errorEmail}</a>{' '}
      if the problem persists.
    </p>
  );
  const notInAnyOrgError = (
    <ErrorPage
      headerContent="You are not associated with a partner organization"
      errorContent={
        <>
          <p>
            You’re not authorized to access this page because your account ({props.userEmail}) isn’t
            linked to a partner organization in our system.
          </p>
          {emailBlurb}
        </>
      }
    />
  );

  const referralDashboardSignUpLink = props.dashboardName === REFERRAL_DASHBOARD_NAME && (
    <p>
      If you are a homebuilder sales associate, click{' '}
      <a target="_blank" rel="noopener noreferrer" href={`${OD_HOMEPAGE_URL}/partners/builders`}>
        here
      </a>{' '}
      to sign up.
    </p>
  );

  const dashboardDisplayStr = props.dashboardName
    ? `the ${props.dashboardName}`
    : 'any partner dashboards';

  const incorrectRoleError = (
    <ErrorPage
      headerContent={`You do not have the correct permissions to access ${dashboardDisplayStr}`}
      errorContent={
        <>
          <p>
            You’re not authorized to access {dashboardDisplayStr} because your account (
            {props.userEmail}) isn’t set up with the right permissions.
          </p>
          {emailBlurb}
          {referralDashboardSignUpLink}
        </>
      }
    />
  );

  if (loading) {
    return props.loadingScreen;
  }

  if (error || !data || !data.organization || !data.organization.partnershipType) {
    // oxlint-disable-next-line typescript/no-deprecated -- Unauthorized page diagnostics move to internal observability in the package migration
    trackEvent('view', 'page', {
      path: location.pathname,
      unauthorized: true,
      unauthorizedReason: 'not_in_partner_org',
      userEmail: props.userEmail,
    });
    return notInAnyOrgError;
  }

  // oxlint-disable-next-line typescript/no-deprecated -- Unauthorized page diagnostics move to internal observability in the package migration
  trackEvent('view', 'page', {
    path: location.pathname,
    unauthorized: true,
    unauthorizedReason: 'incorrect_role',
    userEmail: props.userEmail,
  });
  return incorrectRoleError;
};

export default UnauthorizedAccessErrorPage;
